/**
 * Who is a Compatio admin — the ONE derivation of that role check.
 *
 * **This module used to be `themeGate.ts`,** and it gated the dark theme: `Pages/GMS` was
 * ~5% semantic tokens, so dark meant dark chrome around light pages on most screens, and
 * the theme shipped only to the people migrating the palette. That gate is now **removed** —
 * theme switching is available to every signed-in user, and `Layout` no longer passes
 * `allowDark`, so `system` resolves to the user's actual OS preference for everyone.
 *
 * The role check itself survives because a different surface needs it: the **Design system**
 * entry in the profile menu. That page is an internal reference for the people building the
 * system, not a feature of the product, so it is the admin-only surface now.
 *
 * The module was renamed rather than left in place with its old name and new meaning. A file
 * called `themeGate` that no longer gates the theme is the kind of thing that gets believed.
 *
 * Roles are compared case-insensitively: the string arrives from the API
 * (`parties[].usertypes[].roles[].name`) rather than from a frontend enum, so its casing is
 * not ours to guarantee.
 */
export const ADMIN_ROLES: readonly string[] = ['compatioadmin', 'compatiosuperadmin'];

/** The slice of `AuthManager.getAuthUser()` this reads. */
type AuthUserLike = {
  current_party_id?: unknown;
  profile?: {
    parties?: Array<{
      gms_uid?: unknown;
      usertypes?: Array<{ roles?: Array<{ name?: unknown }> }>;
    }>;
  };
} | null;

/**
 * The current party's first role name, `''` when any link in the chain is missing — the
 * same derivation the profile menu displays in its Role row. Named for what it returns
 * rather than for what one caller does with it, which is why it outlived the theme gate
 * unchanged.
 */
export const currentRoleOf = (userObj: AuthUserLike): string => {
  const currentParty = userObj?.profile?.parties?.find(
    (party) => party?.gms_uid === userObj?.current_party_id,
  );
  const name = currentParty?.usertypes?.[0]?.roles?.[0]?.name;
  return typeof name === 'string' ? name : '';
};

export const isCompatioAdmin = (userObj: AuthUserLike): boolean =>
  ADMIN_ROLES.includes(currentRoleOf(userObj).toLowerCase());
